import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FolderPlus, Upload, Play, Trash2, Film, 
  ArrowLeft, Plus, Folder, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function BRollLibraryPage() {
  const { orientation } = useParams(); // 'vertical' or 'horizontal'
  const navigate = useNavigate();
  const { activeBrand } = useApp();
  
  const [folders, setFolders] = useState([
    // Example default folders
    { id: '1', name: orientation === 'vertical' ? 'Gym' : 'Travel', clips: [] },
    { id: '2', name: orientation === 'vertical' ? 'Cooking' : 'Office', clips: [] },
    { id: '3', name: orientation === 'vertical' ? 'Driving' : 'Nature', clips: [] },
  ]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  const orientationLabel = orientation === 'vertical' ? 'Vertical' : 'Horizontal';
  const aspectRatio = orientation === 'vertical' ? '9/16' : '16/9';

  // Create new folder
  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Enter a folder name');
      return;
    }
    const newFolder = {
      id: Date.now().toString(),
      name: newFolderName.trim(),
      clips: []
    };
    setFolders(prev => [...prev, newFolder]);
    setNewFolderName('');
    setShowNewFolder(false);
    toast.success(`Folder "${newFolder.name}" created`);
  };

  // Delete folder
  const handleDeleteFolder = (folderId) => {
    setFolders(prev => prev.filter(f => f.id !== folderId));
    if (selectedFolder?.id === folderId) {
      setSelectedFolder(null);
    }
    toast.success('Folder deleted');
  };

  // Upload clips to folder
  const handleClipUpload = (folderId, files) => {
    if (!activeBrand) {
      toast.error('Please select a brand first');
      return;
    }

    const newClips = Array.from(files).map((file, i) => ({
      id: `${Date.now()}-${i}`,
      name: file.name,
      file: file,
      thumbnail: null
    }));

    setFolders(prev => prev.map(f => 
      f.id === folderId 
        ? { ...f, clips: [...f.clips, ...newClips] }
        : f
    ));

    // Update selected folder if it's the one being modified
    if (selectedFolder?.id === folderId) {
      setSelectedFolder(prev => ({ ...prev, clips: [...prev.clips, ...newClips] }));
    }
    
    toast.success(`${newClips.length} B-Roll clip(s) added`);
  };

  // Delete clip
  const handleDeleteClip = (folderId, clipId) => {
    setFolders(prev => prev.map(f => 
      f.id === folderId 
        ? { ...f, clips: f.clips.filter(c => c.id !== clipId) }
        : f
    ));
    if (selectedFolder?.id === folderId) {
      setSelectedFolder(prev => ({ ...prev, clips: prev.clips.filter(c => c.id !== clipId) }));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm mb-4 hover:opacity-80"
          style={{ color: 'hsl(var(--foreground-muted))' }}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'hsl(var(--foreground))' }}>
          B-Roll Library - {orientationLabel}
        </h1>
        <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
          Organize your {orientationLabel.toLowerCase()} B-Roll clips by category. The AI will use folder names to understand when to insert them.
        </p>
        {activeBrand && (
          <p className="text-xs mt-2" style={{ color: 'hsl(var(--primary))' }}>
            Brand: {activeBrand.name}
          </p>
        )}
      </div>

      {/* Folders Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
            Folders ({folders.length})
          </h2>
          <Button size="sm" onClick={() => setShowNewFolder(true)}>
            <FolderPlus size={16} className="mr-2" />
            New Folder
          </Button>
        </div>

        {/* New folder input */}
        {showNewFolder && (
          <div className="mb-4 flex gap-2 p-4 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
            <Input
              placeholder="Folder name (e.g., Gym, Cooking, Travel)"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
            />
            <Button onClick={handleCreateFolder}>Create</Button>
            <Button variant="ghost" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>
              Cancel
            </Button>
          </div>
        )}

        {/* Folders grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {folders.map(folder => (
            <div 
              key={folder.id}
              className={`rounded-xl overflow-hidden transition-all ${selectedFolder?.id === folder.id ? 'ring-2 ring-primary' : ''}`}
              style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}
            >
              <div 
                className="p-4 cursor-pointer"
                onClick={() => setSelectedFolder(selectedFolder?.id === folder.id ? null : folder)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Folder size={20} style={{ color: 'hsl(var(--primary))' }} />
                    <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                      {folder.name}
                    </span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="p-1 rounded hover:bg-surface-hover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={16} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleDeleteFolder(folder.id)}>
                        <Trash2 size={14} className="mr-2" style={{ color: 'hsl(var(--destructive))' }} />
                        Delete Folder
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-xs mt-2" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  {folder.clips.length} clips
                </p>
              </div>

              {/* Quick upload */}
              <label 
                className="block p-2 mx-3 mb-3 rounded-lg cursor-pointer text-center transition-colors hover:bg-surface-hover"
                style={{ border: '1px dashed hsl(var(--border))' }}
              >
                <input 
                  type="file" 
                  accept="video/*" 
                  multiple
                  className="hidden"
                  onChange={(e) => handleClipUpload(folder.id, e.target.files)}
                />
                <Plus size={14} className="mx-auto" style={{ color: 'hsl(var(--foreground-subtle))' }} />
                <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  Add
                </span>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Folder Clips */}
      {selectedFolder && (
        <div className="p-6 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              {selectedFolder.name} - B-Roll Clips
            </h3>
            <label className="cursor-pointer">
              <input 
                type="file" 
                accept="video/*" 
                multiple
                className="hidden"
                onChange={(e) => handleClipUpload(selectedFolder.id, e.target.files)}
              />
              <Button size="sm" as="span">
                <Upload size={14} className="mr-2" />
                Upload Clips
              </Button>
            </label>
          </div>

          {selectedFolder.clips.length === 0 ? (
            <div className="text-center py-12">
              <Film size={40} className="mx-auto mb-3" style={{ color: 'hsl(var(--foreground-subtle))' }} />
              <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
                No clips in this folder
              </p>
              <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                Upload {orientationLabel.toLowerCase()} B-Roll clips for "{selectedFolder.name}"
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {selectedFolder.clips.map(clip => (
                <div key={clip.id} className="group relative">
                  <div 
                    className="rounded-lg overflow-hidden"
                    style={{ aspectRatio, background: 'hsl(var(--surface-elevated))' }}
                  >
                    {clip.thumbnail ? (
                      <img src={clip.thumbnail} alt={clip.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film size={24} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button className="p-2 rounded-full bg-white/20 hover:bg-white/30">
                        <Play size={16} className="text-white" />
                      </button>
                      <button 
                        className="p-2 rounded-full bg-red-500/80 hover:bg-red-500"
                        onClick={() => handleDeleteClip(selectedFolder.id, clip.id)}
                      >
                        <Trash2 size={16} className="text-white" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs mt-1 truncate" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    {clip.name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info box */}
      <div className="mt-8 p-4 rounded-xl" style={{ background: 'hsl(var(--primary) / 0.05)', border: '1px solid hsl(var(--primary) / 0.2)' }}>
        <p className="text-sm" style={{ color: 'hsl(var(--foreground))' }}>
          <strong>How B-Roll works:</strong>
        </p>
        <p className="text-xs mt-2" style={{ color: 'hsl(var(--foreground-muted))' }}>
          The AI analyzes your folder names to understand context. For example, if you're editing a fitness video and mention "gym", 
          the AI will automatically suggest or insert clips from your "Gym" folder. Organize your B-Roll by topic for best results.
        </p>
      </div>
    </div>
  );
}
